import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import {
  Fingerprint, ArrowLeft, X, ShieldCheck, ShieldAlert, Trash2, Plus,
  UserCheck, Tag, Building2, ListChecks, Loader2, Award, Check, Eye, EyeOff, Download,
} from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";
import FaceDocumentUpload from "components/FaceDocumentUpload";
import { maskIdentity } from "lib/identity";

const KINDS = ["email", "cpf", "phone", "discord", "external"];

const TIER_STYLE = {
  bronze: "border-amber-700/40 text-amber-700",
  silver: "border-slate-400/50 text-slate-400",
  gold: "border-yellow-500/50 text-yellow-500",
};
export function TierBadge({ tier, t }) {
  const tv = tier || "bronze";
  const label = { bronze: t("bio.bronze", "Bronze"), silver: t("bio.silver", "Prata"), gold: t("bio.gold", "Ouro") }[tv] || tv;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs ${TIER_STYLE[tv] || TIER_STYLE.bronze}`}><Award className="h-3 w-3" />{label}</span>;
}

function Chip({ children, tone }) {
  const cls = tone === "green" ? "border-emerald-500/40 text-emerald-500"
    : tone === "red" ? "border-red-500/40 text-red-500"
    : "border-border text-muted-foreground";
  return <span className={`px-2 py-0.5 rounded-md border text-xs ${cls}`}>{children}</span>;
}

function Section({ icon: Icon, title, count, action, children }) {
  return (
    <Card className="mb-4">
      <CardHeader className="flex-row items-center gap-2 pb-3">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {typeof count === "number" && (
          <span className="text-xs text-muted-foreground">({count})</span>
        )}
        {action && <div className="ml-auto">{action}</div>}
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

// Full-page detail of one biometric person (identities, per-company consent,
// enrollments, recent sessions, de-enroll / LGPD danger zone). Opened from the
// biometrics list; mirrors the sec4us_manager PeopleDetail full-page layout.
export default function BiometricPersonDetail() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [sel, setSel] = useState(null);
  const [doc, setDoc] = useState(null);   // verified document (photo + fields, obfuscated by default)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [revealedIds, setRevealedIds] = useState(() => new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [enrollLink, setEnrollLink] = useState(null);   // {enrollment_url, qr}
  const [addKind, setAddKind] = useState("email");
  const [addVal, setAddVal] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/biometric-people/${id}/`);
      setSel(r.data);
    } catch (e) {
      setError(e?.response?.status === 404
        ? t("bio.notFound", "Person not found.")
        : t("bio.loadFailed", "Failed to load."));
    } finally { setLoading(false); }
  }, [id, t]);

  // The verified document is fetched separately (it decrypts photo + identity fields).
  const loadDocument = useCallback(async (reveal) => {
    try {
      const r = await api.get(`/api/biometric-people/${id}/document/${reveal ? "?reveal=1" : ""}`);
      setDoc(r.data?.has_document ? r.data : null);
    } catch { setDoc(null); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDocument(false); }, [loadDocument]);

  // Re-enrollment from this page: reuse the person's first identity + company.
  const genEnrollLink = async () => {
    const ident = (sel?.identities || [])[0];
    const comp = (sel?.companies || [])[0];
    if (!ident || !comp) return;
    try {
      const r = await api.post("/api/biometric-people/enroll-link/", {
        name: sel.name || "", identity_type: ident.type, identity_value: ident.value,
        company_id: comp.id,
      });
      setEnrollLink(r.data);
    } catch (e) {
      setConfirm({ title: t("bio.error", "Error"), body: e?.response?.data?.detail || String(e), onOk: null });
    }
  };

  // The original file is only available while the review is pending (deleted on decision).
  const downloadOriginal = async () => {
    try {
      const r = await api.get(`/api/biometric-people/${id}/document/original/`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc?.doc_type ? `${doc.doc_type}-${id.slice(0, 8)}` : "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      setConfirm({ title: t("bio.error", "Error"), body: e?.response?.data?.detail || String(e), onOk: null });
    }
  };

  const act = async (payload) => {
    try {
      const r = await api.post(`/api/biometric-people/${id}/action/`, payload);
      if (r.data?.deleted) { navigate("/liveness/admin/biometrics"); return; }
      setSel(r.data);
    } catch (e) {
      setConfirm({ title: t("bio.error", "Error"), body: e?.response?.data?.detail || String(e), onOk: null });
    }
  };

  if (user && !user.capabilities?.manage_system) return <Navigate to="/liveness/sessions" replace />;

  if (loading) {
    return <div className="flex justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (error || !sel) {
    return (
      <div className="animate-fade-in w-full">
        <button onClick={() => navigate("/liveness/admin/biometrics")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" /> {t("bio.back", "Back to biometrics")}
        </button>
        <div className="text-center py-16 text-sm text-muted-foreground">
          <X className="h-10 w-10 mx-auto mb-3 text-red-500" />{error || t("bio.notFound", "Person not found.")}
        </div>
      </div>
    );
  }

  const sessions = sel.sessions || [];
  const authSessions = sessions.filter((s) => s.mode === "authenticate" || s.mode === "kyc" || s.authenticated != null);
  const lastAuth = authSessions[0] || null;   // sessions arrive ordered by -created
  // GitHub-style strong confirmation: the admin must type this exact phrase.
  const dangerPhrase = (sel.name || "").trim() || sel.id.slice(0, 8);

  return (
    <div className="animate-fade-in w-full">
      <button onClick={() => navigate("/liveness/admin/biometrics")}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5">
        <ArrowLeft className="h-4 w-4" /> {t("bio.back", "Back to biometrics")}
      </button>

      {/* Profile header */}
      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              {sel.photo ? (
                <img src={sel.photo} alt={sel.name || "face"}
                  className="h-20 w-20 rounded-xl object-cover border border-border bg-black" />
              ) : (
                <div className="h-20 w-20 rounded-xl border border-border bg-muted flex items-center justify-center">
                  <Fingerprint className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">
                  {sel.name || sel.id.slice(0, 8)}
                </h1>
                <p className="text-xs font-mono text-muted-foreground mt-1">{sel.id}</p>
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                  <span>{t("bio.authCount", "Authentications")}: <strong className="text-foreground">{authSessions.length}</strong></span>
                  <span>{t("bio.readings", "Readings")}: <strong className="text-foreground">{sel.enrollments.length}</strong></span>
                  {lastAuth && <span>{t("bio.lastAuth", "Last")}: <strong className="text-foreground">{lastAuth.created ? lastAuth.created.slice(0, 10) : "—"}</strong></span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {sel.enrolled
                ? <Chip tone="green">{t("bio.enrolled", "Enrolled")}</Chip>
                : <Chip>{t("bio.notEnrolled", "Not enrolled")}</Chip>}
              {sel.key_removed
                ? <Chip tone="red">{t("bio.purged", "Purged (LGPD)")}</Chip>
                : <Chip tone="green">{sel.status}</Chip>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trust tier: bronze -> silver (human) -> gold (document) */}
      <Card className="mb-4">
        <CardHeader className="flex-row items-center gap-2 pb-3">
          <Award className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold tracking-tight">{t("bio.tier", "Nível de confiança")}</h2>
          <TierBadge tier={sel.tier} t={t} />
          {sel.enrolled && (
            <div className="ml-auto">
              <FaceDocumentUpload endpoint={`/api/biometric-people/${id}/document/`} compact
                onGold={() => { load(); loadDocument(false); }} />
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <p className="text-xs text-muted-foreground">
            {t("bio.tierHelp", "Bronze: inicial. Prata: confirmada por um humano. Ouro: documento nacional válido verificado.")}
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            {sel.tier !== "silver" && sel.tier !== "gold" && (
              <Button size="sm" variant="outline" onClick={() => act({ action: "set_tier", tier: "silver" })}>
                <Check className="h-4 w-4 mr-1" />{t("bio.confirmSilver", "Confirmar (Prata)")}
              </Button>
            )}
            {sel.tier === "silver" && (
              <Button size="sm" variant="outline" onClick={() => act({ action: "set_tier", tier: "bronze" })}>
                {t("bio.revertBronze", "Reverter para Bronze")}
              </Button>
            )}
          </div>

          {/* Document under manual review (face match landed in the REVIEW band) */}
          {doc?.review_status === "pending" && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[16rem]">
                <div className="text-sm font-medium text-amber-500">{t("bio.reviewTitle", "Documento aguardando revisão")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("bio.reviewBody", "A similaridade entre a foto do documento e o rosto cadastrado ficou na faixa de revisão.")}
                  {doc.face_similarity != null ? ` (${Math.round(doc.face_similarity * 100)}%)` : ""}
                </div>
              </div>
              <div className="flex gap-2">
                {doc.has_original && (
                  <Button size="sm" variant="outline" onClick={downloadOriginal}>
                    <Download className="h-4 w-4 mr-1" />{t("bio.downloadDoc", "Baixar documento")}
                  </Button>
                )}
                <Button size="sm" variant="outline" className="text-red-500 border-red-500/40 hover:bg-red-500/10"
                  onClick={async () => { await act({ action: "document_review", decision: "reject" }); loadDocument(false); }}>
                  {t("bio.reject", "Rejeitar")}
                </Button>
                <Button size="sm"
                  onClick={async () => { await act({ action: "document_review", decision: "approve" }); loadDocument(false); }}>
                  <Check className="h-4 w-4 mr-1" />{t("bio.approve", "Aprovar")}
                </Button>
              </div>
            </div>
          )}

          {/* Approved document: official photo + identity fields (obfuscated by default) */}
          {doc?.has_document && (
            <div className="flex flex-col sm:flex-row gap-4 pt-1">
              {doc.photo
                ? <img src={doc.photo} alt={t("bio.docPhoto", "Foto do documento")}
                    className="h-36 w-28 object-cover rounded-lg border border-border shrink-0" />
                : <div className="h-36 w-28 rounded-lg border border-dashed border-border grid place-items-center text-[10px] text-muted-foreground text-center px-1 shrink-0">
                    {t("bio.noDocPhoto", "Sem foto")}
                  </div>}
              <div className="flex-1 min-w-0">
                <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-muted-foreground">{t("bio.docName", "Nome completo")}</dt>
                  <dd className="font-mono truncate">{doc.full_name || "—"}</dd>
                  <dt className="text-muted-foreground">{t("bio.docCpf", "CPF")}</dt>
                  <dd className="font-mono">{doc.cpf || "—"}</dd>
                  <dt className="text-muted-foreground">{t("bio.docBirth", "Nascimento")}</dt>
                  <dd className="font-mono">{doc.birth_date || "—"}</dd>
                  <dt className="text-muted-foreground">{t("bio.goldDoc", "Documento")}</dt>
                  <dd className="truncate">{doc.doc_type || "—"}{doc.template ? ` · ${doc.template}` : ""}</dd>
                  <dt className="text-muted-foreground">{t("bio.goldExpiry", "Validade")}</dt>
                  <dd>{doc.expires_on || "—"}</dd>
                  <dt className="text-muted-foreground">{t("bio.docPhotoSrc", "Foto usada")}</dt>
                  <dd className="truncate">
                    {doc.photo_source === "visible"
                      ? t("bio.docPhotoVisible", "impressa em alta resolução (validada contra a assinada)")
                      : t("bio.docPhotoQr", "do QR (assinada)")}
                    {doc.photo_ncc != null ? ` · NCC ${Math.round(doc.photo_ncc * 100)}%` : ""}
                    {doc.photo_ssim != null ? ` · SSIM ${Math.round(doc.photo_ssim * 100)}%` : ""}
                  </dd>
                  <dt className="text-muted-foreground">{t("bio.docSigner", "Assinado por")}</dt>
                  <dd className="truncate">{doc.signer || "—"}{doc.signature_algorithm ? ` · ${doc.signature_algorithm}` : ""}</dd>
                  <dt className="text-muted-foreground">{t("bio.docCert", "Certificado")}</dt>
                  <dd className="truncate font-mono text-xs" title={doc.certificate_id}>
                    {doc.certificate_id || "—"}{doc.certificate_group ? ` (${doc.certificate_group})` : ""}
                  </dd>
                  <dt className="text-muted-foreground">{t("bio.docThumb", "Thumbprint")}</dt>
                  <dd className="truncate font-mono text-xs" title={doc.thumbprint}>{doc.thumbprint || "—"}</dd>
                  {doc.certificate_public_key && (
                    <>
                      <dt className="text-muted-foreground">{t("bio.docPubKey", "Chave pública")}</dt>
                      <dd className="truncate font-mono text-xs" title={doc.certificate_public_key}>
                        {doc.certificate_public_key.slice(0, 28)}…
                      </dd>
                    </>
                  )}
                </dl>
                <Button size="sm" variant="outline" className="mt-3"
                  onClick={() => loadDocument(!doc.revealed)}>
                  {doc.revealed
                    ? <><EyeOff className="h-4 w-4 mr-1" />{t("bio.hideData", "Ocultar dados")}</>
                    : <><Eye className="h-4 w-4 mr-1" />{t("bio.revealData", "Ver dados completos")}</>}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Identities */}
      <Section icon={Tag} title={t("bio.identities", "Identities")} count={sel.identities.length}
        action={<Button size="sm" variant="outline" onClick={() => { setAddVal(""); setAddOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" />{t("bio.addIdentity", "Adicionar identidade")}
        </Button>}>
        <div className="flex flex-wrap gap-2 mb-3">
          {sel.identities.map((i, k) => {
            const shown = revealedIds.has(k);
            return (
              <span key={k} title={i.type}
                className="group inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-xs">
                <span className="font-mono">{shown ? i.value : maskIdentity(i.type, i.value)}</span>
                {i.verified
                  ? <ShieldCheck className="h-3 w-3 text-emerald-500 shrink-0"
                      title={t(`bio.via.${i.verified_via || "admin"}`, t("bio.identVerified", "Verificada"))} />
                  : <ShieldAlert className="h-3 w-3 text-amber-500 shrink-0"
                      title={t("bio.identUnverified", "Não verificada — não é exposta aos consumidores")} />}
                <button type="button"
                  title={shown ? t("bio.hideData", "Ocultar dados") : t("bio.revealData", "Ver dados completos")}
                  onClick={() => setRevealedIds((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; })}
                  className="ml-1 p-1 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-muted-foreground hover:text-foreground hover:bg-muted">
                  {shown ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </button>
                {/* Removal is destructive and sits next to the reveal button — always confirm. */}
                <button type="button" title={t("bio.removeIdentity", "Remover identidade")}
                  onClick={() => setConfirm({
                    title: t("bio.removeIdentity", "Remover identidade"),
                    body: t("bio.removeIdentityBody", "Remover {id} desta biometria? A pessoa deixa de ser encontrada por esta identidade.")
                      .replace("{id}", `${i.type}: ${maskIdentity(i.type, i.value)}`),
                    onOk: () => act({ action: "remove_identity", type: i.type, value: i.value }),
                  })}
                  className="ml-0.5 p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10">
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          {!sel.identities.length && <span className="text-xs text-muted-foreground">—</span>}
        </div>
      </Section>

      {/* Companies + consent */}
      <Section icon={Building2} title={t("bio.companies", "Companies & consent")} count={sel.companies.length}>
        <div className="space-y-2">
          {sel.companies.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <span className="text-sm">{c.name}</span>
              <button onClick={() => act({ action: "consent", company_id: c.id, consent: !c.consent })}
                className={`inline-flex items-center gap-1 text-xs font-medium ${c.consent ? "text-emerald-500" : "text-red-500"}`}>
                {c.consent ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
                {c.consent ? t("bio.consentOn", "Consent granted") : t("bio.consentOff", "No consent")}
              </button>
            </div>
          ))}
          {!sel.companies.length && <span className="text-xs text-muted-foreground">—</span>}
        </div>
      </Section>

      {/* Authentications / sessions */}
      <Section icon={ListChecks} title={t("bio.authentications", "Recent authentications")} count={sessions.length}>
        {sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("bio.noSessions", "No sessions yet.")}</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 text-left">{t("bio.session", "Session")}</th>
                  <th className="px-2 py-1.5 text-left">{t("bio.date", "Date")}</th>
                  <th className="px-2 py-1.5 text-left">{t("bio.mode", "Mode")}</th>
                  <th className="px-2 py-1.5 text-left">{t("bio.status", "Status")}</th>
                  <th className="px-2 py-1.5 text-left">{t("bio.result", "Result")}</th>
                  <th className="px-2 py-1.5 text-right">{t("bio.score", "Score")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sessions.slice(0, 25).map((s) => (
                  <tr key={s.id} className="hover:bg-muted/30">
                    <td className="px-2 py-2 align-middle">
                      <a className="font-mono text-primary hover:underline" href={`/liveness/sessions/${s.id}`}>{s.id.slice(0, 8)}</a>
                    </td>
                    <td className="px-2 py-2 align-middle tabular-nums text-muted-foreground">
                      {s.created ? s.created.slice(0, 19).replace("T", " ") : "—"}
                    </td>
                    <td className="px-2 py-2 align-middle"><Chip>{s.mode}</Chip></td>
                    <td className="px-2 py-2 align-middle text-muted-foreground">{s.status}</td>
                    <td className="px-2 py-2 align-middle">
                      {s.authenticated == null
                        ? <span className="text-muted-foreground">—</span>
                        : s.authenticated
                          ? <Chip tone="green">{t("bio.match", "Match")}</Chip>
                          : <Chip tone="red">{t("bio.noMatch", "No match")}</Chip>}
                    </td>
                    <td className="px-2 py-2 align-middle text-right tabular-nums">
                      {s.auth_match_score != null ? `${Math.round(s.auth_match_score * 100)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Enrollments */}
      <Section icon={UserCheck} title={t("bio.enrollments", "Enrollments")} count={sel.enrollments.length}
        action={!sel.enrolled && (
          <div className="flex gap-2">
            {sel.enrollments.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => act({ action: "reactivate_enrollment" })}>
                <UserCheck className="h-4 w-4 mr-1" />{t("bio.reactivate", "Reativar cadastro anterior")}
              </Button>
            )}
            <Button size="sm" onClick={genEnrollLink} disabled={!sel.identities.length || !sel.companies.length}>
              <Plus className="h-4 w-4 mr-1" />{t("bio.newEnrollLink", "Gerar link de cadastro")}
            </Button>
          </div>
        )}>
        <div className="space-y-1 text-xs">
          {sel.enrollments.map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-muted-foreground">
              {e.active
                ? <UserCheck className="h-3.5 w-3.5 text-emerald-500" />
                : <span className="w-3.5" />}
              {e.recognizer || "—"} · det {Number(e.det_score).toFixed(2)} · {e.encrypted ? t("bio.encrypted", "encrypted") : t("bio.plaintext", "plaintext")} · {e.created.slice(0, 19).replace("T", " ")}
              {!e.active && <span className="text-amber-500">({t("bio.inactive", "inativo")})</span>}
            </div>
          ))}
          {!sel.enrollments.length && <span className="text-muted-foreground">—</span>}
        </div>
      </Section>

      {/* Document read attempts (Super Admin audit) */}
      {!!(sel.document_attempts && sel.document_attempts.length) && (
        <Card className="mb-4">
          <CardHeader className="flex-row items-center gap-2 pb-3">
            <ListChecks className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold tracking-tight">{t("bio.docAttempts", "Tentativas de leitura de documento")}</h2>
            <span className="text-xs text-muted-foreground">({sel.document_attempts.length})</span>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left">{t("bio.date", "Data")}</th>
                    <th className="px-2 py-1.5 text-left">{t("bio.docFile", "Arquivo")}</th>
                    <th className="px-2 py-1.5 text-left">{t("bio.docResult", "Resultado")}</th>
                    <th className="px-2 py-1.5 text-right">{t("bio.docSim", "Similaridade")}</th>
                    <th className="px-2 py-1.5 text-left">{t("bio.docBy", "Por")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sel.document_attempts.map((a) => (
                    <tr key={a.id} className="hover:bg-muted/30">
                      <td className="px-2 py-2 align-middle tabular-nums text-muted-foreground">{a.created ? a.created.slice(0, 19).replace("T", " ") : "—"}</td>
                      <td className="px-2 py-2 align-middle truncate max-w-[16ch]" title={a.filename}>{a.filename || "—"}{a.doc_type ? ` · ${a.doc_type}` : ""}</td>
                      <td className="px-2 py-2 align-middle">
                        {a.ok
                          ? <span className="inline-flex items-center gap-1 text-emerald-500 text-xs"><Check className="h-3 w-3" />{t("doc.gold", "Ouro")}</span>
                          : <span className="text-red-500 text-xs">{t(`doc.reason.${{not_authentic:"notAuthentic",sandbox_document:"sandbox",expired_too_long:"expired",no_expiry:"noExpiry",parse_failed:"parseFailed",library_unavailable:"unavailable",empty:"empty",face_mismatch:"faceMismatch",photo_unavailable:"photoUnavailable",doc_no_face:"docNoFace",no_enrollment:"noEnrollment"}[a.reason] || "parseFailed"}`, a.reason)}</span>}
                      </td>
                      <td className="px-2 py-2 align-middle text-right tabular-nums">{a.similarity != null ? `${Math.round(a.similarity * 100)}%` : "—"}</td>
                      <td className="px-2 py-2 align-middle text-muted-foreground truncate max-w-[16ch]">{a.by_user || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Governance: enable/disable + blacklist */}
      <Card className="mb-4">
        <CardHeader className="flex-row items-center gap-2 pb-3">
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold tracking-tight">{t("bio.governance", "Governança")}</h2>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">{t("bio.enabledLabel", "Biometria habilitada")}</div>
              <div className="text-xs text-muted-foreground">{t("bio.enabledHelp", "Desabilite para impedir o uso em autenticação sem apagar o cadastro.")}</div>
            </div>
            <button type="button" onClick={() => act({ action: "set_disabled", disabled: !sel.disabled })}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm ${sel.disabled ? "border-amber-500/40 text-amber-500" : "border-emerald-500/40 text-emerald-500"}`}>
              {sel.disabled ? t("bio.disabled", "Desabilitada") : t("bio.enabled", "Habilitada")}
            </button>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">{t("bio.blacklist", "Blacklist")}</div>
              <div className="text-xs text-muted-foreground">{t("bio.blacklistHelp", "Negar todo uso, ou apenas alertar o Super Admin a cada uso.")}</div>
            </div>
            <select value={sel.blacklist_mode || ""}
              onChange={(e) => act({ action: "blacklist", mode: e.target.value })}
              className="h-9 rounded-lg border border-border bg-background text-sm px-2">
              <option value="">{t("bio.blNone", "Normal")}</option>
              <option value="deny">{t("bio.blDeny", "Negar todo uso")}</option>
              <option value="alert">{t("bio.blAlert", "Somente alertar")}</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="mb-4 border-red-500/30">
        <CardHeader className="flex-row items-center gap-2 pb-3">
          <Trash2 className="h-4 w-4 text-red-500" />
          <h2 className="text-sm font-semibold tracking-tight">{t("bio.dangerZone", "Danger zone")}</h2>
        </CardHeader>
        <CardContent className="pt-0 flex flex-wrap gap-2">
          {sel.enrolled && (
            <Button size="sm" variant="outline"
              onClick={() => setConfirm({
                title: t("bio.deenrollTitle", "Remover cadastro biométrico"),
                body: t("bio.deenrollBody", "Remove o template facial desta pessoa — ela deixa de autenticar até se cadastrar novamente."),
                phrase: dangerPhrase,
                onOk: () => act({ action: "deenroll" }),
              })}>
              {t("bio.deenroll", "De-enroll (remove template)")}
            </Button>
          )}
          {!sel.key_removed && (
            <Button size="sm" variant="outline" className="text-red-500 border-red-500/40 hover:bg-red-500/10"
              onClick={() => setConfirm({
                title: t("bio.lgpdTitle", "LGPD removal"),
                body: t("bio.lgpdBody", "This encrypts and retains a minimal record, e-mails the recovery key to the subject, then permanently deletes the server key. Irreversible."),
                needEmail: true,
                phrase: dangerPhrase,
                onOk: (email) => act({ action: "lgpd", email }),
              })}>
              <Trash2 className="h-4 w-4 mr-1" />{t("bio.lgpd", "LGPD remove")}
            </Button>
          )}
          {!sel.enrolled && (
            <Button size="sm" variant="outline" className="text-red-500 border-red-500/40 hover:bg-red-500/10"
              onClick={() => setConfirm({
                title: t("bio.hardDeleteTitle", "Remover biometria"),
                body: t("bio.hardDeleteBody", "Remove permanentemente esta biometria não utilizada (sem cadastro e sem histórico de uso). Ação irreversível."),
                phrase: dangerPhrase,
                onOk: () => act({ action: "hard_delete" }),
              })}>
              <Trash2 className="h-4 w-4 mr-1" />{t("bio.hardDelete", "Remover (hard-delete)")}
            </Button>
          )}
          {!sel.enrolled && sel.key_removed && (
            <span className="text-xs text-muted-foreground">{t("bio.noActions", "No actions available.")}</span>
          )}
        </CardContent>
      </Card>

      {confirm && createPortal(
        <ConfirmModal c={confirm} t={t} onClose={() => setConfirm(null)} />, document.body)}

      {enrollLink && createPortal(
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl p-5">
            <h2 className="font-semibold mb-1">{t("bio.newEnrollLink", "Gerar link de cadastro")}</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t("bio.enrollLinkHelp", "Abra este link (ou escaneie o QR) no celular para refazer o cadastro biométrico.")}
            </p>
            {enrollLink.qr && <img src={enrollLink.qr} alt="QR" className="h-40 w-40 mx-auto mb-3 rounded-lg bg-white p-1" />}
            <input readOnly value={enrollLink.enrollment_url || ""}
              onFocus={(e) => e.target.select()}
              className="w-full h-10 rounded-lg border border-border bg-muted text-xs font-mono px-3" />
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="outline"
                onClick={() => navigator.clipboard?.writeText(enrollLink.enrollment_url || "")}>
                {t("clientAppForm.copy", "Copiar")}
              </Button>
              <Button size="sm" onClick={() => { setEnrollLink(null); load(); }}>{t("common.close", "Fechar")}</Button>
            </div>
          </div>
        </div>, document.body)}

      {addOpen && createPortal(
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl p-5">
            <h2 className="font-semibold mb-1">{t("bio.addIdentity", "Adicionar identidade")}</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t("bio.addIdentityHelp", "Vincule um e-mail, CPF, telefone ou id externo a esta biometria. Cada identidade é única em todo o sistema.")}
            </p>
            <div className="space-y-3">
              <select value={addKind} onChange={(e) => setAddKind(e.target.value)}
                className="w-full h-10 rounded-lg border border-border bg-background text-sm px-2">
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <input value={addVal} onChange={(e) => setAddVal(e.target.value)} autoFocus
                placeholder={t("bio.identityValue", "value")}
                onKeyDown={(e) => { if (e.key === "Enter" && addVal.trim()) { act({ action: "add_identity", type: addKind, value: addVal.trim() }); setAddOpen(false); } }}
                className="w-full h-10 rounded-lg border border-border bg-background text-sm px-3" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button size="sm" variant="outline" onClick={() => setAddOpen(false)}>{t("bio.cancel", "Cancel")}</Button>
              <Button size="sm" disabled={!addVal.trim()}
                onClick={() => { act({ action: "add_identity", type: addKind, value: addVal.trim() }); setAddOpen(false); }}>
                <Plus className="h-4 w-4 mr-1" />{t("bio.add", "Add")}
              </Button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}

function ConfirmModal({ c, t, onClose }) {
  const [email, setEmail] = useState("");
  const [typed, setTyped] = useState("");
  // Strong confirmation (GitHub style): destructive actions require typing an exact phrase.
  const phraseOk = !c.phrase || typed.trim() === c.phrase;
  const blocked = (c.needEmail && !email.trim()) || !phraseOk;
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl p-5">
        <h2 className="font-semibold mb-2">{c.title}</h2>
        <p className="text-sm text-muted-foreground mb-4">{c.body}</p>
        {c.needEmail && (
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder={t("bio.escrowEmail", "Subject e-mail (escrow key)")}
            className="w-full h-10 mb-4 rounded-lg border border-border bg-background text-sm px-3" />
        )}
        {c.phrase && (
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-1.5">
              {t("bio.typeToConfirm", "Para confirmar, digite")} <code className="px-1 py-0.5 rounded bg-muted text-foreground font-mono select-all">{c.phrase}</code>
            </p>
            <input type="text" value={typed} onChange={(e) => setTyped(e.target.value)}
              autoComplete="off" spellCheck={false} placeholder={c.phrase}
              className={`w-full h-10 rounded-lg border bg-background text-sm px-3 font-mono ${typed && !phraseOk ? "border-red-500/60" : "border-border"}`} />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>{t("bio.cancel", "Cancel")}</Button>
          {c.onOk && (
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white"
              disabled={blocked}
              onClick={() => { c.onOk(email.trim()); onClose(); }}>{t("bio.confirm", "Confirm")}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
